import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, MenuItem, Paper, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  DEFAULT_LOYALTY_CONFIG, formatMinor, toMajor, toMinor,
  type LoyaltyAccount, type LoyaltyConfig, type LoyaltyLedgerEntry, type LoyaltyTier,
} from '@eprintsmart/shared';
import {
  getLoyaltyAccount, getLoyaltyLedger, saveLoyaltyConfig, watchLoyaltyConfig,
} from '../services/loyalty';

/**
 * Print Credits configuration.
 *
 * The per-tier earn rate lives here and nowhere a customer can reach it — this
 * doc is admin-only, so a rate change never ships to the storefront bundle. The
 * page is deliberately blunt about that: the rates are internal.
 */
export function Loyalty() {
  const [config, setConfig] = useState<LoyaltyConfig | null>(null);

  useEffect(() => watchLoyaltyConfig(setConfig), []);

  if (!config) {
    return <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h1">Print Credits</Typography>
      <Alert severity="info">
        Earn rates are <strong>internal</strong>. Customers only ever see a ₹ balance, their tier
        and how far they are from the next tier — never a percentage, and never a "you'll earn"
        figure before they buy.
      </Alert>

      <Button
        component={RouterLink}
        to="/loyalty/customers"
        variant="outlined"
        sx={{ alignSelf: 'flex-start' }}
      >
        View customer credit board →
      </Button>

      <ConfigForm config={config} />
      <Divider />
      <AccountLookup />
    </Stack>
  );
}

const TIER_ORDER: LoyaltyTier[] = ['SILVER', 'GOLD', 'PLATINUM'];

function ConfigForm({ config }: { config: LoyaltyConfig }) {
  const [form, setForm] = useState<LoyaltyConfig>(config);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(config); }, [config]);

  const setTier = (tier: LoyaltyTier, patch: Partial<LoyaltyConfig['tiers'][number]>) =>
    setForm((f) => ({
      ...f,
      tiers: f.tiers.map((t) => (t.tier === tier ? { ...t, ...patch } : t)),
    }));

  const save = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      await saveLoyaltyConfig({
        tiers: form.tiers,
        earnBase: form.earnBase,
        redeemCapBps: form.redeemCapBps,
        minRedeemMinor: form.minRedeemMinor,
        expiryMonths: form.expiryMonths,
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  // Tiers, sorted so Silver → Gold → Platinum always read in order.
  const tiers = TIER_ORDER
    .map((t) => form.tiers.find((x) => x.tier === t))
    .filter((t): t is LoyaltyConfig['tiers'][number] => Boolean(t));

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={3}>
        {error && <Alert severity="error">{error}</Alert>}
        {saved && <Alert severity="success">Saved.</Alert>}

        <Box>
          <Typography variant="h6" gutterBottom>Tiers &amp; earn rates <Chip size="small" label="internal" color="warning" sx={{ ml: 1 }} /></Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tier</TableCell>
                <TableCell>Label (shown)</TableCell>
                <TableCell align="right">Earn rate&nbsp;%</TableCell>
                <TableCell align="right">Reaches at 12-mo spend (₹)</TableCell>
                <TableCell>Perks (shown, one per line)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tiers.map((t) => (
                <TableRow key={t.tier}>
                  <TableCell><strong>{t.tier}</strong></TableCell>
                  <TableCell>
                    <TextField size="small" value={t.label}
                      onChange={(e) => setTier(t.tier, { label: e.target.value })} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField size="small" type="number" sx={{ width: 90 }}
                      value={t.earnRateBps / 100}
                      onChange={(e) => setTier(t.tier, { earnRateBps: Math.round(Number(e.target.value) * 100) })}
                      inputProps={{ step: 0.05, min: 0 }} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField size="small" type="number" sx={{ width: 130 }}
                      value={toMajor(t.minSpend12mMinor)}
                      disabled={t.tier === 'SILVER'}
                      onChange={(e) => setTier(t.tier, { minSpend12mMinor: toMinor(Number(e.target.value)) })} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth multiline value={t.perks.join('\n')}
                      onChange={(e) => setTier(t.tier, { perks: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom>Rules</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField label="Earn base" select value={form.earnBase} sx={{ width: 260 }}
              onChange={(e) => setForm((f) => ({ ...f, earnBase: e.target.value as LoyaltyConfig['earnBase'] }))}
              helperText="What the rate is a % of">
              <MenuItem value="NET_ITEMS_EX_TAX">Item value, after discount, ex-GST</MenuItem>
              <MenuItem value="GRAND_TOTAL">Grand total (incl. GST)</MenuItem>
            </TextField>
            <TextField label="Redeem cap %" type="number" sx={{ width: 150 }}
              value={form.redeemCapBps / 100}
              onChange={(e) => setForm((f) => ({ ...f, redeemCapBps: Math.round(Number(e.target.value) * 100) }))}
              helperText="Max % of an order credits may cover" inputProps={{ min: 0, max: 100 }} />
            <TextField label="Min redeem (₹)" type="number" sx={{ width: 150 }}
              value={toMajor(form.minRedeemMinor)}
              onChange={(e) => setForm((f) => ({ ...f, minRedeemMinor: toMinor(Number(e.target.value)) }))}
              helperText="Balance below this can't be spent" />
            <TextField label="Expiry (months)" type="number" sx={{ width: 150 }}
              value={form.expiryMonths}
              onChange={(e) => setForm((f) => ({ ...f, expiryMonths: Number(e.target.value) }))}
              helperText="Credits expire after" inputProps={{ min: 1, max: 120 }} />
          </Stack>
        </Box>

        <Stack direction="row" spacing={2}>
          <Button variant="contained" onClick={save} disabled={saving}>Save settings</Button>
          <Button color="inherit" onClick={() => setForm(DEFAULT_LOYALTY_CONFIG)}>Reset to defaults</Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/** Read-only lookup of a customer's wallet, for support questions. Keyed by the
 * auth uid, which the CRM contact row already carries. */
function AccountLookup() {
  const [uid, setUid] = useState('');
  const [account, setAccount] = useState<LoyaltyAccount | null>(null);
  const [ledger, setLedger] = useState<LoyaltyLedgerEntry[]>([]);
  const [state, setState] = useState<'idle' | 'loading' | 'empty' | 'done'>('idle');

  const look = async () => {
    const id = uid.trim();
    if (!id) return;
    setState('loading');
    const acc = await getLoyaltyAccount(id);
    if (!acc) { setAccount(null); setLedger([]); setState('empty'); return; }
    setAccount(acc);
    setLedger(await getLoyaltyLedger(id));
    setState('done');
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Customer lookup</Typography>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField size="small" label="Customer uid" value={uid} sx={{ flex: 1 }}
          onChange={(e) => setUid(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && look()}
          helperText="The auth uid — shown on the customer's CRM row" />
        <Button variant="outlined" onClick={look} disabled={state === 'loading'}>Look up</Button>
      </Stack>

      {state === 'empty' && <Alert severity="info">No Print Credits wallet for that uid yet.</Alert>}

      {state === 'done' && account && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={3}>
            <Metric label="Balance" value={formatMinor(account.balanceMinor)} />
            <Metric label="Tier" value={account.tier} />
            <Metric label="12-mo spend" value={formatMinor(account.spend12mMinor)} />
            <Metric label="Lifetime earned" value={formatMinor(account.lifetimeEarnedMinor)} />
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ledger.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{new Date(e.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={e.type} /></TableCell>
                  <TableCell align="right" sx={{ color: e.amountMinor < 0 ? 'error.main' : 'success.main' }}>
                    {e.amountMinor < 0 ? '−' : '+'}{formatMinor(Math.abs(e.amountMinor))}
                  </TableCell>
                  <TableCell>{e.note ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>
      )}
    </Paper>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="h6">{value}</Typography>
    </Box>
  );
}
